import { formatDistanceToNow, format, isValid, parseISO } from 'date-fns';

export const formatDate = (date: Date | string | number | null | undefined, formatString: string = 'MMM dd, yyyy'): string => {
    if (!date) return '';

    try {
        const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
        if (!isValid(dateObj)) return '';
        return format(dateObj, formatString);
    } catch {
        return '';
    }
};

export const formatDateTime = (date: Date | string | number | null | undefined, formatString: string = 'MMM dd, yyyy HH:mm'): string => {
    return formatDate(date, formatString);
};

export const formatTime = (date: Date | string | number | null | undefined): string => {
    return formatDate(date, 'HH:mm:ss');
};

export const formatTimeAgo = (date: Date | string | number | null | undefined): string => {
    if (!date) return '';

    try {
        const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
        if (!isValid(dateObj)) return '';
        return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch {
        return '';
    }
};

export const formatDuration = (seconds: number): string => {
    if (seconds < 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const formatDurationHuman = (seconds: number): string => {
    if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

    return parts.join(', ');
};

export const formatScore = (score: number, total: number): string => {
    return `${score}/${total}`;
};

export const formatPercentage = (value: number, total: number = 100, decimals: number = 0): string => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return `${percentage.toFixed(decimals)}%`;
};

export const formatNumber = (num: number, decimals: number = 0): string => {
    return num.toFixed(decimals);
};

export const formatLargeNumber = (num: number): string => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatCurrency = (amount: number, currency: string = 'USD', locale: string = 'en-US'): string => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    }).format(amount);
};

export const truncateText = (text: string, maxLength: number, suffix: string = '...'): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - suffix.length) + suffix;
};

export const capitalizeFirst = (text: string): string => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
};

export const capitalizeWords = (text: string): string => {
    if (!text) return '';
    return text
        .split(' ')
        .map((word) => capitalizeFirst(word))
        .join(' ');
};

export const slugify = (text: string): string => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    if (cleaned.length === 11 && cleaned[0] === '1') {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    return phone;
};

export const formatInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const formatGrade = (
    score: number,
    total: number,
    gradingScale: 'percentage' | 'letter_grades' | 'points' = 'percentage',
): string => {
    const percentage = total > 0 ? (score / total) * 100 : 0;

    switch (gradingScale) {
        case 'letter_grades':
            if (percentage >= 90) return 'A';
            if (percentage >= 80) return 'B';
            if (percentage >= 70) return 'C';
            if (percentage >= 60) return 'D';
            return 'F';
        case 'points':
            return `${score.toFixed(0)}/${total.toFixed(0)}`;
        case 'percentage':
        default:
            return `${percentage.toFixed(1)}%`;
    }
};

export const formatList = (items: string[], conjunction: string = 'and'): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items[items.length - 1]}`;
};
